import { supabase } from './supabase';

/**
 * Stripe: browser → supabase.functions.invoke (Edge Functions).
 *
 * The checkout function SLUG must match the name in Supabase Dashboard (URL ends with
 * /functions/v1/<slug>). If the slug is wrong, the gateway returns 404 and the browser often
 * reports it as a CORS preflight error.
 *
 * Override with REACT_APP_SUPABASE_STRIPE_CHECKOUT_FUNCTION if your deployed name differs
 * (default here matches a common dashboard name "deploy-stripe-checkout").
 *
 * For JWT/CORS with anon invoke: supabase/config.toml (verify_jwt = false) then redeploy both.
 */
const STRIPE_CHECKOUT_FUNCTION =
  process.env.REACT_APP_SUPABASE_STRIPE_CHECKOUT_FUNCTION || 'deploy-stripe-checkout';

/**
 * @param {object} body
 * @param {number} body.amount - major currency units (e.g. GBP £123.45)
 * @param {string} [body.currency]
 * @param {string} body.hotelName
 * @param {string} body.propertyId
 * @param {string} body.resumeKey
 * @param {string} body.cancelPath - e.g. /payment/uuid
 * @param {string} [body.successPath] - path after pay (default /booking-confirmation on server); use e.g. /flights/payment/success for flights
 * @returns {Promise<string>} Stripe Checkout URL
 */
export async function createStripeCheckoutSession(body) {
  const siteOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : undefined;
  const payload = siteOrigin ? { ...body, siteOrigin } : body;

  const { data, error } = await supabase.functions.invoke(STRIPE_CHECKOUT_FUNCTION, {
    body: payload,
  });

  if (error) {
    throw new Error(error.message || 'Could not start Stripe checkout');
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  if (!data?.url) {
    throw new Error('No checkout URL returned from server');
  }
  return data.url;
}

/**
 * @param {string} sessionId - cs_...
 * @param {object} [options]
 * @param {string} [options.expectedResumeKey] - must match Checkout Session metadata.resumeKey (flight/hotel flow)
 * @param {string} [options.expectedPropertyId] - must match metadata.propertyId (offer id / property id)
 * @returns {Promise<{ ok: boolean, error?: string, payment_status?: string }>}
 */
export async function verifyStripeCheckoutSession(sessionId, options = {}) {
  const body = { session_id: sessionId };
  if (options.expectedResumeKey) {
    body.expected_resume_key = options.expectedResumeKey;
  }
  if (options.expectedPropertyId) {
    body.expected_property_id = options.expectedPropertyId;
  }

  const { data, error } = await supabase.functions.invoke('stripe-verify-session', {
    body,
  });

  if (error) {
    return { ok: false, error: error.message };
  }
  if (data?.error && !data?.ok) {
    return { ok: false, error: data.error };
  }
  if (data?.ok === true) {
    return { ok: true, ...data };
  }
  return { ok: false, error: data?.error || 'Verification failed' };
}
